import type {
  ThemePagesQueryResponse,
  ThemePagesQueryVariables,
} from '~/api/graphql/queries/themePages.generated';
import type { ThemePageEdgeSelectFragment } from '~/api/graphql/fragments/themePage.generated';
import type { ThemePageStatus } from '~/types/graphql';
import type { IThemePage } from '~/stores/themePage';

import { ThemePagesDocument } from '~/api/graphql/queries/themePages.generated';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useQueryThemeInfo } from './useQueryThemeInfo';
import { appFetcher } from '~/api/utils/gatewayClient';
import { IThemePageType } from '~/types/themePage';
import { useEffect, useMemo } from 'react';
import { mapThemePagesV7 } from '~/utils';

interface UseQueryGPThemePagesByStatusProps {
  enabled: boolean;
  statusIn: ThemePageStatus[];
}

interface UseQueryGPThemePagesByStatusResult {
  themePages: IThemePage[];
  isFetching: boolean;
}

const GEMX_SUPPORTED_GP_PAGE_TYPES = [
  IThemePageType.GP_ARTICLE,
  IThemePageType.GP_COLLECTION,
  IThemePageType.GP_PRODUCT,
  IThemePageType.GP_BLOG,
  IThemePageType.GP_INDEX,
  IThemePageType.GP_STATIC,
  IThemePageType.GP_FUNNEL_PAGE,
  IThemePageType.GP_PRE_SALE_PAGE,
];

const PAGE_SIZE = 100;

export const useQueryGPThemePagesByStatus = ({
  enabled,
  statusIn,
}: UseQueryGPThemePagesByStatusProps): UseQueryGPThemePagesByStatusResult => {
  const { themeId } = useQueryThemeInfo();

  const baseVariables: ThemePagesQueryVariables = useMemo(
    () => ({
      where: {
        themeID: themeId ?? '',
        statusIn,
        typeIn: GEMX_SUPPORTED_GP_PAGE_TYPES,
      },
      first: PAGE_SIZE,
    }),
    [themeId, statusIn],
  );

  const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery<
    ThemePagesQueryResponse,
    unknown
  >({
    queryKey: ['ThemePages', themeId, baseVariables],
    queryFn: ({ pageParam }) =>
      appFetcher<ThemePagesQueryResponse, ThemePagesQueryVariables>(ThemePagesDocument, {
        ...baseVariables,
        after: pageParam as string | undefined,
      })(),
    getNextPageParam: (lastPage) => {
      const pageInfo = lastPage?.themePages?.pageInfo;
      return pageInfo?.hasNextPage ? pageInfo.endCursor : undefined;
    },
    enabled: !!themeId && enabled,
  });

  useEffect(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const themePages = useMemo(() => {
    const allEdges = data?.pages.flatMap(
      (page) => (page?.themePages?.edges as ThemePageEdgeSelectFragment[]) ?? [],
    );
    return mapThemePagesV7(allEdges);
  }, [data]);

  return {
    themePages,
    isFetching: isFetching || !!hasNextPage,
  };
};
